import { useEffect, useState } from "react";

import type { ReactNode } from "react";
import type { OAuthProvider } from "@canvaschool/contracts";

import { ApiError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import {
  ErrorAlert,
  errorCodeOf,
  errorMessage,
} from "../components/ErrorAlert";
import { Link } from "../components/Link";
import { Logo } from "../components/Logo";
import { SITE_NAME_WITH_FORMER } from "../seo/site";

import type { ApiErrorCode } from "../api/client";
import type { AnonymousNotice } from "../auth/AuthProvider";

/** Failures that must look identical so login IDs cannot be enumerated. */
const CREDENTIAL_FAILURES = new Set<ApiErrorCode>([
  "INVALID_CREDENTIALS",
  "VALIDATION_FAILED",
]);

const NOTICES: Record<AnonymousNotice, string> = {
  PASSWORD_CHANGED:
    "비밀번호를 변경했습니다. 새 비밀번호로 다시 로그인해 주세요.",
};

const formatWait = (seconds: number): string =>
  seconds >= 60
    ? `${Math.floor(seconds / 60)}분 ${seconds % 60}초`
    : `${seconds}초`;

/** Login-only wording: says what a repeated mistake leads to. */
const loginErrorMessage = (code: ApiErrorCode, blocked: boolean): string => {
  if (blocked) {
    return "로그인 시도가 많아 잠시 막혔습니다. 버튼의 시간이 지난 뒤 아이디와 비밀번호를 확인하고 다시 시도해 주세요. 또 틀리면 더 오래 막힙니다.";
  }
  return code === "INVALID_CREDENTIALS"
    ? `${errorMessage(code)} 여러 번 틀리면 잠시 로그인이 막힙니다.`
    : errorMessage(code);
};

export const LoginForm = ({
  initialError,
  notice = null,
  compact = false,
}: {
  initialError: ApiErrorCode | null;
  notice?: AnonymousNotice | null;
  compact?: boolean;
}) => {
  const auth = useAuth();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<ApiErrorCode | null>(initialError);
  const [pending, setPending] = useState(false);
  const [blockedUntil, setBlockedUntil] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now);
  const waitSeconds =
    blockedUntil === null
      ? 0
      : Math.max(0, Math.ceil((blockedUntil - now) / 1_000));

  useEffect(() => {
    if (blockedUntil === null) {
      return;
    }
    const timer = window.setInterval(() => {
      if (Date.now() >= blockedUntil) {
        setBlockedUntil(null);
        setError(null);
      }
      setNow(Date.now());
    }, 1_000);
    return () => window.clearInterval(timer);
  }, [blockedUntil]);

  return (
    <>
      {notice && (
        <p className="canvaschool-success" role="status">
          {NOTICES[notice]}
        </p>
      )}
      <form
        aria-label="로그인"
        className="canvaschool-form"
        onSubmit={async (event) => {
          event.preventDefault();
          setPending(true);
          setError(null);
          try {
            await auth.login(loginId, password);
          } catch (failure) {
            const code = errorCodeOf(failure);
            setError(
              CREDENTIAL_FAILURES.has(code) ? "INVALID_CREDENTIALS" : code,
            );
            if (failure instanceof ApiError && failure.retryAfterSeconds) {
              setNow(Date.now());
              setBlockedUntil(Date.now() + failure.retryAfterSeconds * 1_000);
            }
            setPending(false);
          }
        }}
      >
        <div className="canvaschool-field">
          <label htmlFor="canvaschool-login-id">아이디</label>
          <input
            id="canvaschool-login-id"
            autoComplete="username"
            value={loginId}
            onChange={(event) => setLoginId(event.target.value)}
            required
          />
        </div>
        <div className="canvaschool-field">
          <label htmlFor="canvaschool-password">비밀번호</label>
          <input
            id="canvaschool-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />
        </div>
        {error && (
          <ErrorAlert
            code={error}
            boxed
            message={loginErrorMessage(error, waitSeconds > 0)}
          />
        )}
        <button
          type="submit"
          className="canvaschool-btn-primary"
          disabled={pending || waitSeconds > 0}
        >
          {waitSeconds > 0
            ? `${formatWait(waitSeconds)} 후 다시 시도`
            : "로그인"}
        </button>
      </form>
      {!compact && (
        <p className="canvaschool-hint canvaschool-login-note">
          학생 계정은 선생님이 만들어 드립니다.
        </p>
      )}
    </>
  );
};

/** `?oauth_error=` from the sign-in callback (apps/backend oauthRoutes). */
const OAUTH_ERRORS: Record<string, string> = {
  FAILED: "로그인을 마치지 못했습니다. 처음부터 다시 시도해 주세요.",
  UNAVAILABLE: "지금은 이 방법으로 로그인할 수 없습니다.",
  DISABLED: "사용이 중지된 계정입니다. 관리자에게 문의해 주세요.",
  CONFLICT:
    "이 이메일의 크루바스 계정은 이미 다른 계정과 연결되어 있습니다. 아이디와 비밀번호로 로그인해 주세요.",
};

const PROVIDERS: Record<
  OAuthProvider,
  { path: string; label: string; mark: ReactNode }
> = {
  GOOGLE: {
    path: "/api/v1/auth/oauth/google/start",
    label: "구글로 로그인하기",
    mark: (
      <svg viewBox="0 0 48 48" width="20" height="20" aria-hidden="true">
        <path
          fill="#EA4335"
          d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
        />
        <path
          fill="#4285F4"
          d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
        />
        <path
          fill="#FBBC05"
          d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
        />
        <path
          fill="#34A853"
          d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
        />
      </svg>
    ),
  },
  NAVER: {
    path: "/api/v1/auth/oauth/naver/start",
    label: "네이버로 로그인하기",
    mark: (
      <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M13.56 10.7 6.17 0H0v20h6.44V9.3L13.83 20H20V0h-6.44z"
        />
      </svg>
    ),
  },
};

/** Google·Naver sign-in for teachers; renders nothing until the server
 * says which providers it has. */
const OAuthButtons = () => {
  const auth = useAuth();
  const [providers, setProviders] = useState<OAuthProvider[]>([]);
  const [oauthError] = useState(
    () =>
      OAUTH_ERRORS[
        new URLSearchParams(window.location.search).get("oauth_error") ?? ""
      ] ?? null,
  );

  useEffect(() => {
    let live = true;
    auth.api.listOAuthProviders().then(
      (list) => live && setProviders(list),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [auth.api]);

  return (
    <div className="canvaschool-oauth">
      {oauthError && (
        <p className="canvaschool-error" role="alert">
          {oauthError}
        </p>
      )}
      {providers.map((provider) => (
        <a
          key={provider}
          href={PROVIDERS[provider].path}
          className={`canvaschool-oauth-btn canvaschool-oauth-btn--${provider.toLowerCase()}`}
        >
          {PROVIDERS[provider].mark}
          {PROVIDERS[provider].label}
        </a>
      ))}
      {providers.length > 0 && (
        <p className="canvaschool-hint">
          선생님은 구글 계정으로 바로 시작할 수 있습니다. 처음 로그인하면 교사
          계정이 만들어집니다.
        </p>
      )}
    </div>
  );
};

export const LoginPage = ({
  initialError,
  notice = null,
}: {
  initialError: ApiErrorCode | null;
  notice?: AnonymousNotice | null;
}) => (
  <section className="canvaschool-auth canvaschool-login">
    <div className="canvaschool-login-hero">
      <Logo size={40} />
      <h1>크루바스</h1>
      <p>수업의 시선이 정렬되는 곳</p>
      <p>{SITE_NAME_WITH_FORMER}</p>
    </div>
    {notice && (
      <p className="canvaschool-success" role="status">
        {NOTICES[notice]}
      </p>
    )}
    <OAuthButtons />
    <p className="canvaschool-hint">
      학생과 아이디를 받은 선생님은 아이디와 비밀번호로 로그인해 주세요.
    </p>
    <LoginForm initialError={initialError} />
    <p className="canvaschool-hint canvaschool-login-manual">
      처음이신가요? <Link to="/manual">이용 방법 보기</Link>
    </p>
  </section>
);
